import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Plus, Save } from "lucide-react";
import { Field } from "./shared";
import { NEW_LEDGER_OPTION, fundTypeLabels } from "./constants";

export default function FundForm({ form, setForm, availableLedgers, ledgerLocked, busy, onSubmit, onCancel, submitLabel, isEdit }) {
  const openingLocked = ledgerLocked && form.linkedLedgerId !== NEW_LEDGER_OPTION;
  return (
    <div>
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="Fund name">
          <Input value={form.fundName} onChange={(e) => setForm((f) => ({ ...f, fundName: e.target.value }))} placeholder="Sinking fund" />
        </Field>
        <Field label="Fund code (optional)">
          <Input value={form.fundCode} onChange={(e) => setForm((f) => ({ ...f, fundCode: e.target.value }))} />
        </Field>
        <Field label="Fund type">
          <Select value={form.fundType} onValueChange={(v) => setForm((f) => ({ ...f, fundType: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(fundTypeLabels).map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Start date (optional)">
          <Input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} />
        </Field>
        <Field label="Linked ledger">
          <Select disabled={ledgerLocked} value={form.linkedLedgerId} onValueChange={(v) => setForm((f) => ({ ...f, linkedLedgerId: v }))}>
            <SelectTrigger><SelectValue placeholder="Select a Fund-group ledger" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NEW_LEDGER_OPTION}>+ Create a new ledger for this fund</SelectItem>
              {availableLedgers.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {ledgerLocked && <p className="mt-1 text-xs text-amber-600">Locked: posted vouchers already reference this ledger.</p>}
        </Field>
        {form.linkedLedgerId === NEW_LEDGER_OPTION && !ledgerLocked && (
          <Field label="New ledger name">
            <Input value={form.newLedgerName} onChange={(e) => setForm((f) => ({ ...f, newLedgerName: e.target.value }))} placeholder={form.fundName || "e.g. Sinking fund"} />
          </Field>
        )}
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-xs text-[#374151]">
            <input type="checkbox" className="h-3.5 w-3.5 rounded-sm border-[#D1D5DB]" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
            Active
          </label>
        </div>
      </div>
      <div className="mt-4 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Opening Balance</p>
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Amount already held">
            <Input
              type="number" min="0" step="0.01" disabled={openingLocked}
              value={form.openingBalance} onChange={(e) => setForm((f) => ({ ...f, openingBalance: e.target.value }))}
              placeholder="0.00"
            />
          </Field>
          <Field label="Balance is">
            <Select value={form.openingBalanceSide} onValueChange={(v) => setForm((f) => ({ ...f, openingBalanceSide: v }))} disabled={openingLocked}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Cr (typical - money set aside)</SelectItem>
                <SelectItem value="debit">Dr</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="As of date">
            <Input type="date" disabled={openingLocked} value={form.openingAsOfDate} onChange={(e) => setForm((f) => ({ ...f, openingAsOfDate: e.target.value }))} />
          </Field>
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">
          {openingLocked
            ? "This fund's ledger already has posted transactions, so its opening balance can no longer be changed here."
            : "What this fund already held as of the date above. Saved immediately with the fund - no separate posting step."}
        </p>
      </div>

      <div className="mt-3">
        <Field label="Description">
          <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
        </Field>
      </div>
      <div className="mt-3">
        <Field label="Notes">
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
        </Field>
      </div>
      <div className="mt-3 flex gap-2">
        <Button disabled={busy} onClick={onSubmit} className="bg-[#111827] text-white hover:bg-[#374151]">
          {isEdit ? <><Save className="mr-2 h-4 w-4" /> {submitLabel || "Save changes"}</> : <><Plus className="mr-2 h-4 w-4" /> {submitLabel || "Add fund"}</>}
        </Button>
        {onCancel && <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
}
